"use client";

import type { ActionButtonProps } from "@/components/ActionButton";
import { ActionGroup } from "@/components/ActionGroup";
import { AdaptiveText, type AdaptiveTextProps } from "@/components/Content";
import type { IconName } from "@/components/Icon";
import Icon from "@/components/Icon";
import { PlusGrid, PlusGridItem, PlusGridRow } from "@/components/PlusGrid";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { useNavbar } from "@/context/NavbarContext";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useState } from "react";
import MobileNavbar from "./MobileNavbar";

export type NavFlyoutItem = {
	name: string;
	description: AdaptiveTextProps["portableText"];
	href: string;
	icon?: IconName;
	image?: string;
	external?: boolean;
};

export type NavItem = {
	name: string;
} & (
	| {
			type: "link";
			href: string;
			external?: boolean;
	  }
	| {
			type: "flyout";
			items: NavFlyoutItem[];
			actions?: ActionButtonProps[];
	  }
);

export type NavbarProps = {
	logoUrl: string;
	logoDarkUrl: string;
	items: NavItem[];
	actions: ActionButtonProps[];
};

export const customTriggerStyle = () =>
	cn(
		// Base styles
		"group inline-flex h-auto w-max items-center justify-center",
		"rounded-md bg-transparent px-4 py-3",
		"text-base font-medium text-gray-950",
		"transition-colors",

		// Hover state - more transparent green
		"hover:bg-primary/5",
		"hover:text-gray-950",

		// Focus states
		"focus:outline-none",
		"focus:bg-primary/5",
		"focus:text-gray-950",

		// Disabled state
		"disabled:pointer-events-none disabled:opacity-50",

		// Open/active states with transparent green shades
		"data-[state=open]:bg-primary/5",
		"data-[state=open]:text-gray-950",
		"data-[state=open]:hover:bg-primary/10",
		"data-[state=open]:focus:bg-primary/15",

		// Override any potential accent colors from the base component
		"data-[active=true]:bg-primary/5",
		"data-[active=true]:text-gray-950",
		"data-[active=true]:hover:bg-primary/10",
		"data-[active=true]:focus:bg-primary/15",
	);

const NavFlyout = ({
	name,
	items,
	actions,
	maxItems = 6,
	onOpenChange,
}: {
	name: string;
	items: NavFlyoutItem[];
	actions?: ActionButtonProps[];
	maxItems?: number;
	onOpenChange?: (open: boolean) => void;
}) => {
	const pathname = usePathname();

	return (
		<>
			<NavigationMenuTrigger className={customTriggerStyle()}>
				{name}
			</NavigationMenuTrigger>
			<NavigationMenuContent className="flex flex-col rounded-xl p-4 shadow-lg md:w-[400px] lg:w-[500px]">
				{items.slice(0, maxItems).map((item, index) => {
					const isActiveListItem = pathname.includes(item.href);

					let itemIcon: React.ReactElement | null = null;

					if (item.image) {
						itemIcon = (
							<Image
								src={item.image}
								alt={item.name}
								width={128}
								height={128}
								className="w-10"
							/>
						);
					} else if (item.icon) {
						itemIcon = (
							<div className="flex size-10 items-center justify-center rounded-lg bg-primary/5 text-primary/70 ring-1 ring-primary/30">
								<Icon name={item.icon} className="w-5" />
							</div>
						);
					}

					return (
						<div key={`flyout-${item.name}-${index}`} className="w-full p-0.5">
							<NavigationMenuItem asChild onClick={() => onOpenChange?.(false)}>
								<Link
									className={cn(
										customTriggerStyle(),
										"group flex h-auto w-full items-center justify-start gap-x-4 rounded-lg p-4 hover:bg-primary/5",
										{
											"bg-primary/5 text-primary": isActiveListItem,
										},
									)}
									href={item.href}
									target={item.external ? "_blank" : "_self"}
									rel={item.external ? "noopener noreferrer" : ""}
								>
									{itemIcon}
									<div className="flex flex-1 flex-col gap-y-1">
										<span>{item.name}</span>
										<AdaptiveText
											portableText={item.description}
											className="mt-0 text-gray-600 text-xs group-hover:text-gray-900 sm:text-sm"
										/>
									</div>
								</Link>
							</NavigationMenuItem>
						</div>
					);
				})}

				{actions && actions.length > 0 && (
					<>
						<Separator className="my-3 bg-black/5" />
						<ActionGroup actions={actions} className="mt-2 justify-end" />
					</>
				)}
			</NavigationMenuContent>
		</>
	);
};

const Navbar = ({ logoUrl, logoDarkUrl, items, actions }: NavbarProps) => {
	const [value, setValue] = useState("");
	const pathname = usePathname();
	const { scrolled } = useNavbar();

	const handleFlyoutClose = useCallback(() => {
		setValue("");
	}, []);

	return (
		<>
			{/* Mobile Navbar */}
			<MobileNavbar
				logoUrl={logoUrl}
				logoDarkUrl={logoDarkUrl}
				items={items}
				actions={actions}
			/>

			{/* Desktop Navbar */}
			<header className="fixed inset-x-0 top-0 z-50 hidden transition-all duration-200 xl:block">
				<nav>
					<PlusGrid
						className={cn(
							"relative border-b px-4 transition-all duration-200 md:px-8 lg:px-12",
							{
								"border-black/5 bg-white": scrolled,
								"border-transparent pt-12 sm:pt-16": !scrolled,
							},
						)}
					>
						<PlusGridRow className="relative flex h-full justify-between">
							<div className="relative gap-6">
								<PlusGridItem className="h-full py-3">
									<Link
										href="/"
										title="Home"
										className="flex h-full items-center"
									>
										<Image
											src={logoUrl}
											alt="Memorang"
											width={1024}
											height={1024}
											className="h-9 w-auto"
											quality={100}
										/>
									</Link>
								</PlusGridItem>
							</div>

							<NavigationMenu
								value={value}
								onValueChange={setValue}
								viewport={false}
							>
								<NavigationMenuList className="flex items-center">
									{items.map((item) => (
										<PlusGridItem
											key={item.name}
											className="relative h-full py-3"
										>
											<NavigationMenuItem>
												{item.type === "flyout" ? (
													<NavFlyout
														name={item.name}
														items={item.items}
														actions={item.actions}
														onOpenChange={handleFlyoutClose}
													/>
												) : (
													<Link
														href={item.href}
														target={item.external ? "_blank" : "_self"}
														rel={item.external ? "noopener noreferrer" : ""}
														className={cn(
															"flex items-center px-4 py-3 font-medium text-base bg-blend-multiply data-hover:bg-black/[2.5%]",
															{
																"text-primary": pathname.includes(item.href),
															},
														)}
													>
														{item.name}
													</Link>
												)}
											</NavigationMenuItem>
										</PlusGridItem>
									))}
								</NavigationMenuList>
							</NavigationMenu>

							<div className="relative">
								<PlusGridItem className="flex h-full items-center py-3">
									<ActionGroup actions={actions} buttonClassName="h-full" />
								</PlusGridItem>
							</div>
						</PlusGridRow>
					</PlusGrid>
				</nav>

				{/* Container for SubNavbar portal */}
				<div id="subnav-container" className="-z-10 relative w-full" />
			</header>
		</>
	);
};

export default Navbar;
